declare let window: any;

function analyticsHasLoaded(name) {
  if (!window.analytics) {
    console.warn(`Segment: ${name} has not been loaded on the page`); // eslint-disable-line no-console
    return false;
  }
  return true;
}

// Segment docs: https://segment.com/docs/connections/sources/catalog/libraries/website/javascript/#track
export async function sendSegmentEvent(eventName, eventProperties): Promise<void> {
  function sendEvent(): Promise<void> {
    console.log(`Segment: ${eventName} is loading on the page`); // eslint-disable-line no-console
    return new Promise<void>((resolve) => {
      window.analytics.track(eventName, eventProperties, () => {
        resolve();
      });
    });
  }

  if (analyticsHasLoaded(eventName)) {
    return sendEvent();
  }
  return new Promise<void>((resolve) => {
    const handler = async () => {
      window.removeEventListener('segmentLoaded', handler);
      await sendEvent();
      resolve();
    };
    window.addEventListener('segmentLoaded', handler);
  });
}

/**
 * Sends a Segment event with a timeout to prevent hanging.
 * If the event doesn't complete within the timeout, it fails silently.
 * @param eventName - Name of the event to track
 * @param eventProperties - Properties to include with the event
 * @param timeoutMs - Timeout in milliseconds (default: 2000)
 */
export async function sendSegmentEventWithTimeout(
  eventName: string,
  eventProperties: Record<string, any>,
  timeoutMs: number = 2000
): Promise<void> {
  const timeoutPromise = new Promise<void>((_, reject) => {
    setTimeout(() => reject(new Error('Segment event timeout')), timeoutMs);
  });

  try {
    await Promise.race([sendSegmentEvent(eventName, eventProperties), timeoutPromise]);
  } catch (err) {
    console.warn(`Failed to send segment event: ${eventName}`);
  }
}

// Segment docs: https://segment.com/docs/connections/sources/catalog/libraries/website/javascript/#page
export async function sendSegmentPageview(pageName, pageProperties?) {
  function sendEvent() {
    console.log(`Segment: Send pageview is loading on the page`); // eslint-disable-line no-console
    return window.analytics.page(pageName, pageProperties);
  }

  if (analyticsHasLoaded('Send pageview')) {
    await sendEvent();
  } else {
    const handler = () => {
      window.removeEventListener('segmentLoaded', handler);
      sendEvent();
    };
    window.addEventListener('segmentLoaded', handler);
  }
}

// Segment docs: https://segment.com/docs/connections/sources/catalog/libraries/website/javascript/#identify
export async function identifyUserInSegment(userID, identifyProperties) {
  function sendEvent() {
    console.log(`Segment: Identify user is loading on the page`); // eslint-disable-line no-console
    return window.analytics.identify(userID, identifyProperties);
  }

  if (analyticsHasLoaded('Identify user')) {
    await sendEvent();
  } else {
    const handler = () => {
      window.removeEventListener('segmentLoaded', handler);
      sendEvent();
    };
    window.addEventListener('segmentLoaded', handler);
  }
}
